import { useCallback, useEffect, useRef, useState } from "react";
import * as Sentry from "@sentry/react";

const { logger } = Sentry;

/**
 * Remote endpoints used to verify real internet reachability.
 * We use `no-cors` so the response is opaque, but a successful fetch
 * (no thrown error) means the host was reachable.
 *
 * Order matters: we try the first, fall back to the rest on failure.
 */
const REACHABILITY_ENDPOINTS = [
  "https://www.google.com/generate_204",
  "https://www.gstatic.com/generate_204",
  "https://clients3.google.com/generate_204",
];

const REACHABILITY_CHECK_TIMEOUT_MS = 5000;
const REACHABILITY_POLL_INTERVAL_MS = 15000;

/**
 * Attempts to reach one of the known-good endpoints to verify actual
 * internet connectivity. Returns true if any endpoint is reachable.
 */
const checkReachability = async (): Promise<boolean> => {
  for (const url of REACHABILITY_ENDPOINTS) {
    try {
      const controller = new AbortController();
      const timeoutId = window.setTimeout(
        () => controller.abort(),
        REACHABILITY_CHECK_TIMEOUT_MS,
      );

      // Cache-busting query param to avoid a cached 200/204 masking an outage.
      await fetch(`${url}?_=${Date.now()}`, {
        method: "GET",
        mode: "no-cors",
        cache: "no-store",
        signal: controller.signal,
      });

      window.clearTimeout(timeoutId);
      return true;
    } catch (error) {
      // Try next endpoint
      logger.debug(
        logger.fmt`Reachability check failed for ${url}: ${String(error)}`,
      );
    }
  }
  return false;
};

/**
 * Hook that tracks real internet availability.
 *
 * It combines:
 *  - The browser's `online` / `offline` events (fast, but can be a lie — a
 *    device can be on a captive-portal Wi-Fi and still be considered "online").
 *  - An active reachability check against Google's `generate_204` endpoints.
 *
 * Returns the effective online state plus a manual recheck function.
 */
export const useOnlineStatus = () => {
  const [isOnline, setIsOnline] = useState<boolean>(() =>
    typeof navigator !== "undefined" ? navigator.onLine : true,
  );
  const [isChecking, setIsChecking] = useState<boolean>(false);
  const pollTimerRef = useRef<number | null>(null);
  const inFlightRef = useRef<boolean>(false);

  const runReachabilityCheck = useCallback(async () => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setIsChecking(true);
    try {
      // If the browser says we're offline, skip the network call.
      if (typeof navigator !== "undefined" && !navigator.onLine) {
        setIsOnline(false);
        return;
      }
      const reachable = await checkReachability();
      setIsOnline(reachable);
      if (!reachable) {
        logger.warn("No internet reachability despite navigator.onLine=true");
      }
    } catch (error) {
      Sentry.captureException(error);
      setIsOnline(false);
    } finally {
      inFlightRef.current = false;
      setIsChecking(false);
    }
  }, []);

  useEffect(() => {
    // Initial check so we don't trust `navigator.onLine` blindly on mount.
    runReachabilityCheck();

    const handleOnline = () => {
      // Browser thinks we're back online — verify with an actual request.
      runReachabilityCheck();
    };
    const handleOffline = () => {
      setIsOnline(false);
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        runReachabilityCheck();
      }
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Poll periodically so we also detect captive-portal / dead-Wi-Fi cases.
    pollTimerRef.current = window.setInterval(
      runReachabilityCheck,
      REACHABILITY_POLL_INTERVAL_MS,
    );

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      if (pollTimerRef.current !== null) {
        window.clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
  }, [runReachabilityCheck]);

  return { isOnline, isChecking, recheck: runReachabilityCheck };
};
